import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DollarSign, TrendingUp, Clock, Users, BookOpen,
  PlusCircle, UserPlus, Activity,
} from "lucide-react";
import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

interface Referral {
  id: string;
  client_email: string | null;
  sale_amount: number;
  commission_rate: number;
  commission_amount: number;
  status: string;
  created_at: string;
  paid_at: string | null;
}

interface Booking {
  id: string;
  campaign_name: string | null;
  brand_name: string | null;
  email: string | null;
  total_budget: number | null;
  status: string;
  created_at: string;
}

interface AffiliateOverviewTabProps {
  totalSales: number;
  totalCommission: number;
  pendingCommission: number;
  paidCommission: number;
  activeClients: number;
  totalBookings: number;
  referrals: Referral[];
  bookings: Booking[];
  onTabChange: (tab: string) => void;
}

export const AffiliateOverviewTab = ({
  totalSales, totalCommission, pendingCommission, paidCommission,
  activeClients, totalBookings, referrals, bookings, onTabChange,
}: AffiliateOverviewTabProps) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  // Monthly earnings chart data (last 6 months)
  const monthlyData = useMemo(() => {
    const months: { month: string; earnings: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString(isRTL ? 'ar' : 'en', { month: 'short' });
      const earnings = referrals
        .filter(r => r.created_at.startsWith(key))
        .reduce((s, r) => s + r.commission_amount, 0);
      months.push({ month: label, earnings });
    }
    return months;
  }, [referrals, isRTL]);

  // Recent activity feed
  const recentActivity = useMemo(() => {
    const items: { id: string; type: 'booking' | 'commission'; label: string; date: string; amount?: number }[] = [];
    bookings.slice(0, 5).forEach(b => {
      items.push({
        id: `b-${b.id}`,
        type: 'booking',
        label: b.campaign_name || b.brand_name || (isRTL ? 'حملة' : 'Campaign'),
        date: b.created_at,
        amount: b.total_budget || 0,
      });
    });
    referrals.slice(0, 5).forEach(r => {
      items.push({
        id: `r-${r.id}`,
        type: 'commission',
        label: r.client_email || (isRTL ? 'عمولة' : 'Commission'),
        date: r.created_at,
        amount: r.commission_amount,
      });
    });
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  }, [bookings, referrals, isRTL]);

  const stats = [
    { label: isRTL ? "إجمالي المبيعات" : "Total Sales", value: `${totalSales.toLocaleString()} SAR`, icon: DollarSign, color: "text-green-500", bg: "bg-green-500/10" },
    { label: isRTL ? "إجمالي العمولات" : "Total Commission", value: `${totalCommission.toLocaleString()} SAR`, icon: TrendingUp, color: "text-primary", bg: "bg-primary/10" },
    { label: isRTL ? "العمولات المعلقة" : "Pending", value: `${pendingCommission.toLocaleString()} SAR`, icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
    { label: isRTL ? "المدفوع" : "Paid", value: `${paidCommission.toLocaleString()} SAR`, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-500/10" },
    { label: isRTL ? "العملاء" : "Clients", value: activeClients.toString(), icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: isRTL ? "الحجوزات" : "Bookings", value: totalBookings.toString(), icon: BookOpen, color: "text-purple-500", bg: "bg-purple-500/10" },
  ];

  return (
    <div className="space-y-6" data-tour="aff-overview">
      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i}>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2.5">
                  <div className={`h-9 w-9 rounded-lg ${stat.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
                    <p className="text-lg font-bold truncate">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Earnings Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{isRTL ? "الأرباح الشهرية" : "Monthly Earnings"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: number) => [`${v.toLocaleString()} SAR`, isRTL ? 'الأرباح' : 'Earnings']} />
                  <Bar dataKey="earnings" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{isRTL ? "إجراءات سريعة" : "Quick Actions"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-start gap-2" onClick={() => onTabChange('create')}>
              <PlusCircle className="h-4 w-4" />
              {isRTL ? "إنشاء حملة" : "Create Campaign"}
            </Button>
            <Button variant="outline" className="w-full justify-start gap-2" onClick={() => onTabChange('clients')}>
              <UserPlus className="h-4 w-4" />
              {isRTL ? "إضافة عميل" : "Add Client"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {isRTL ? "النشاط الأخير" : "Recent Activity"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground">{isRTL ? "لا يوجد نشاط بعد" : "No activity yet"}</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map(item => (
                <div key={item.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`h-2 w-2 rounded-full shrink-0 ${item.type === 'booking' ? 'bg-blue-500' : 'bg-green-500'}`} />
                    <span className="truncate">{item.label}</span>
                  </div>
                  <span className="text-muted-foreground text-xs shrink-0 ml-2">
                    {item.amount?.toLocaleString()} SAR
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
